import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { debounce, uniq } from "lodash";
import moment from "moment-timezone";
import { Inbox, Search } from "lucide-react";
import Spinner from "../../../../../../components/shared/Spinner";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../../../../../../components/ui/input-group";
import Avatar from "../../../../../../components/shared/Avatar";
import Checkbox from "../../../../../../components/shared/inputs/Checkbox";
import useApi from "../../../../../../utils/api";
import { useGroups } from "../../../../../Settings/General/OrganisationLevelGroups/context";
import { renderUserName } from "../../../../../../utils/helpers/user";

const QUALIFICATION_FIELDS = ["skills", "licenses", "certifications"] as const;

function UsersDisplay({ shift, selectedUsers, setSelectedUsers }: any) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const { allocatedGroups } = useGroups();
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const { authGet } = useApi();
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false
    });

    const {
        data: users,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading
    } = useInfiniteQuery({
        queryKey: ["/users", { search: debouncedSearch, shift }],
        queryFn: async ({ queryKey, pageParam }: any) => {
            const [endpoint, params] = queryKey;
            const firstSection = params?.shift?.sections?.[0];
            const levels = allocatedGroups?.map((group: any) => group.level.replaceAll("_", ""));

            const filters: any = {
                "filter[locations.locationId][value]": [firstSection?.location?.id],
                "filter[locations.locationId][method]": "exact",
                "filter[jobs.jobId][value]": [firstSection?.job?.id],
                "filter[jobs.jobId][method]": "exact",
                terminated: false
            }
            levels.forEach((level: string) => {
                if (firstSection?.[level]) {
                    filters[`filter[${level}][method]`] = "exact";
                    filters[`filter[${level}][value]`] = [firstSection?.[level]?.id];
                }
            });
            let hasQualifications = false;
            QUALIFICATION_FIELDS.forEach((field) => {
                const ids = uniq(
                    (params?.shift?.sections ?? [])
                        .filter((section: any) => section?.type !== "break")
                        .flatMap((section: any) => section?.[field] ?? [])
                        .map((item: any) => item?.id ?? item)
                        .filter(Boolean)
                );
                if (ids.length === 0) return;
                hasQualifications = field !== "skills";
                filters[`filter[${field}][method]`] = "exact";
                filters[`filter[${field}][value]`] = ids;
            });
            if (hasQualifications) {
                filters.qualifiedFrom = moment(params?.shift?.startDate).format("YYYY-MM-DD");
                filters.qualifiedTo = moment(params?.shift?.endDate).format("YYYY-MM-DD");
            }
            if (params?.search) {
                filters.search = params.search;
            }
            const response = await authGet(endpoint, {
                params: {
                    ...filters,
                    page: pageParam,
                    perPage: 20
                }
            });
            return response;
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const loadedItems = allPages.reduce((acc, page) => acc + (page?.result?.length || 0), 0);
            if (loadedItems < (lastPage?.totalItems || 0)) {
                return allPages.length + 1;
            }
            return undefined;
        },
        enabled: !!shift
    });

    useEffect(() => {
        if (!inView) return;
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView]);

    const data = useMemo(() => {
        if (!users?.pages) return [];
        return users.pages.flatMap((page: any) => page?.result);
    }, [users?.pages]);

    const applySearchFilter = useMemo(
        () =>
            debounce((value) => {
                setDebouncedSearch(value);
            }, 500),
        []
    );

    useEffect(() => {
        return () => applySearchFilter.cancel();
    }, [applySearchFilter]);

    const handleInputChange = (e: any) => {
        const value = e.target.value;
        setInputValue(value);
        applySearchFilter(value);
    };

    const onCheckboxChange = useCallback((userId: string, value: boolean) => {
        setSelectedUsers((prev: any[]) => {
            if (!value) {
                return prev.filter(user => user.id !== userId);
            }

            if (prev.some(user => user.id === userId)) {
                return prev;
            }

            if (prev.length >= shift?.slots) {
                return prev;
            }

            const selectedUser = data.find((u: any) => u.id === userId);
            return selectedUser ? [...prev, selectedUser] : prev;
        });
    }, [setSelectedUsers, shift?.slots, data]);

    return (
        <div className="">
            <InputGroup>
                <InputGroupAddon>
                    <Search />
                </InputGroupAddon>
                <InputGroupInput
                    value={inputValue}
                    placeholder={t("search") + "..."}
                    onChange={handleInputChange}
                />
            </InputGroup>
            <div className="tw-my-4 tw-overflow-auto h-100">
                {isLoading ? (
                    <div className="tw-w-full tw-py-2 tw-flex tw-items-center tw-justify-center">
                        <Spinner className="tw-text-primary" />
                    </div>
                ) : (data?.length === 0 || !data) ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-neutral-500 tw-border tw-rounded-sm tw-py-4 tw-border-dashed tw-mb-2">
                        <Inbox />
                        <span>{t("no-data")}</span>
                    </div>
                ) : data.map((user: any) => {
                    const checked = selectedUsers?.find((selectedUser: any) => selectedUser?.id === user?.id);
                    const disabled = !checked && selectedUsers?.length === shift?.slots;
                    return (
                        <div key={user?.id} className="tw-px-3 tw-flex tw-items-center tw-gap-2 tw-py-2 tw-border-b">
                            <Checkbox
                                checked={!!checked}
                                disabled={disabled}
                                onCheckedChange={(value: boolean) => onCheckboxChange(user.id, value)}
                            />
                            <Avatar user={user} size="sm" textStyles="text-primary" />
                            <p className="tw-font-semibold tw-text-sm tw-text-primary">{renderUserName(user)}</p>
                        </div>
                    );
                })}
                {(hasNextPage && !isLoading && data?.length > 0) && (
                    <div
                        ref={ref}
                        className="tw-py-2 tw-text-center tw-text-sm tw-text-muted-foreground"
                    >
                        {isFetchingNextPage ? t("loading") : t("loading-more")}
                    </div>
                )}
            </div>
        </div>
    );
}

export default UsersDisplay;
